import { supabase } from './supabase';
import algoliasearch from 'algoliasearch';

// Initialize Algolia client with environment variables
const searchClient = algoliasearch(
  import.meta.env.VITE_ALGOLIA_APP_ID || '',
  import.meta.env.VITE_ALGOLIA_SEARCH_KEY || '' // Use search-only key for client-side
);

// Initialize admin client for indexing operations
const adminClient = algoliasearch(
  import.meta.env.VITE_ALGOLIA_APP_ID || '',
  import.meta.env.VITE_ALGOLIA_ADMIN_KEY || ''
);

// Initialize indexes
const indexes = {
  blogPosts: adminClient.initIndex('blog_posts'),
  courses: adminClient.initIndex('courses'),
  myths: adminClient.initIndex('ai_myths'),
  nonprofits: adminClient.initIndex('nonprofit_resources')
};

// Initialize search-only indexes
const searchIndexes = {
  blogPosts: searchClient.initIndex('blog_posts'),
  courses: searchClient.initIndex('courses'),
  myths: searchClient.initIndex('ai_myths'),
  nonprofits: searchClient.initIndex('nonprofit_resources')
};

// Configure index settings
async function configureIndexes() {
  try {
    // Blog Posts Index
    await indexes.blogPosts.setSettings({
      searchableAttributes: [
        'title',
        'content',
        'excerpt',
        'author',
        'category',
        'tags',
        'location'
      ],
      attributesForFaceting: ['category', 'author', 'tags', 'location'],
      customRanking: ['desc(published_at)'],
      attributesToSnippet: [
        'content:100',
        'excerpt:50'
      ],
      // Add synonyms for voice search optimization
      synonyms: [
        {
          objectID: 'non-profit-synonyms',
          type: 'synonym',
          synonyms: ['non-profit', 'nonprofit', 'not-for-profit', 'foundation', 'charity', 'ngo']
        },
        {
          objectID: 'ai-synonyms',
          type: 'synonym',
          synonyms: ['ai', 'artificial intelligence', 'machine learning', 'ml', 'automation']
        },
        {
          objectID: 'midwest-synonyms',
          type: 'synonym',
          synonyms: ['midwest', 'nebraska', 'omaha', 'council bluffs', 'lincoln', 'iowa']
        }
      ]
    });

    // Courses Index
    await indexes.courses.setSettings({
      searchableAttributes: [
        'title',
        'description',
        'content',
        'level',
        'category',
        'location'
      ],
      attributesForFaceting: ['category', 'level', 'tags', 'location'],
      customRanking: ['desc(published_at)'],
      attributesToSnippet: [
        'description:100',
        'content:50'
      ]
    });

    // AI Myths Index
    await indexes.myths.setSettings({
      searchableAttributes: [
        'title',
        'explanation',
        'useCases',
        'statistics'
      ],
      attributesForFaceting: [],
      customRanking: ['asc(title)'],
      attributesToSnippet: [
        'explanation:100',
        'useCases:50'
      ]
    });

    // Non-profit Resources Index
    await indexes.nonprofits.setSettings({
      searchableAttributes: [
        'title',
        'description',
        'content',
        'location',
        'organization_type'
      ],
      attributesForFaceting: ['location', 'organization_type', 'resource_type'],
      customRanking: ['desc(published_at)'],
      attributesToSnippet: [
        'description:100',
        'content:50'
      ],
      // Add synonyms for voice search optimization
      synonyms: [
        {
          objectID: 'location-synonyms',
          type: 'synonym',
          synonyms: ['omaha', 'nebraska', 'council bluffs', 'midwest', 'lincoln', 'iowa']
        },
        {
          objectID: 'organization-synonyms',
          type: 'synonym',
          synonyms: ['foundation', 'non-profit', 'nonprofit', 'grantmaker', 'charity', 'ngo']
        }
      ]
    });
  } catch (error) {
    console.error('Error configuring indexes:', error);
    // Don't throw, allow the process to continue
  }
}

// Function to index courses
async function indexCourses() {
  try {
    // Check if Algolia credentials are configured
    if (!import.meta.env.VITE_ALGOLIA_APP_ID || !import.meta.env.VITE_ALGOLIA_ADMIN_KEY) {
      console.warn('Algolia credentials not configured - skipping indexing');
      return false;
    }

    // Configure indexes first
    await configureIndexes();

    const { data: courses, error } = await supabase
      .from('courses')
      .select(`
        *,
        category:course_categories(*)
      `)
      .eq('is_published', true);

    if (error) throw error;

    const objects = courses?.map(course => ({
      objectID: course.id,
      title: course.title,
      description: course.description,
      content: course.content,
      slug: course.slug,
      level: course.level,
      duration: course.duration,
      price: course.price,
      category: course.category?.name,
      published_at: course.published_at,
      image_url: course.image_url,
      location: 'Omaha, Nebraska',
      type: 'course' // Add type to distinguish in search results
    }));

    if (objects && objects.length > 0) {
      await indexes.courses.saveObjects(objects);
      console.log(`Successfully indexed ${objects.length} courses`);
    }

    return true;
  } catch (error) {
    console.error('Error indexing courses:', error);
    return false;
  }
}

// Function to search content
export async function searchAllContent(query: string) {
  try {
    // Check if Algolia is configured
    if (!import.meta.env.VITE_ALGOLIA_APP_ID || !import.meta.env.VITE_ALGOLIA_SEARCH_KEY) {
      console.warn('Algolia search not configured - returning empty results');
      return {
        blogPosts: [],
        courses: [],
        myths: [],
        nonprofits: [],
        authors: [],
        categories: [],
        surveys: []
      };
    }

    // Initialize indexes if they don't exist
    await configureIndexes();

    // Search all indexes in parallel using search-only client
    const [blogResults, courseResults, mythResults, nonprofitResults] = await Promise.all([
      searchIndexes.blogPosts.search(query, {
        hitsPerPage: 5,
        attributesToRetrieve: [
          'objectID',
          'title',
          'excerpt',
          'slug',
          'author',
          'category',
          'published_at',
          'read_time',
          'location'
        ]
      }),
      searchIndexes.courses.search(query, {
        hitsPerPage: 5,
        attributesToRetrieve: [
          'objectID',
          'title',
          'description',
          'slug',
          'level',
          'duration',
          'price',
          'category',
          'image_url',
          'location'
        ]
      }),
      searchIndexes.myths.search(query, {
        hitsPerPage: 3,
        attributesToRetrieve: [
          'objectID',
          'title',
          'explanation',
          'url'
        ]
      }),
      searchIndexes.nonprofits.search(query, {
        hitsPerPage: 3,
        attributesToRetrieve: [
          'objectID',
          'title',
          'description',
          'url',
          'location',
          'organization_type'
        ]
      })
    ]);

    return {
      blogPosts: blogResults.hits,
      courses: courseResults.hits,
      myths: mythResults.hits,
      nonprofits: nonprofitResults.hits,
      authors: [],
      categories: [],
      surveys: []
    };
  } catch (error) {
    console.error('Error searching content:', error);
    // Return empty results instead of throwing
    return {
      blogPosts: [],
      courses: [],
      myths: [],
      nonprofits: [],
      authors: [],
      categories: [],
      surveys: []
    };
  }
}

// Function to index non-profit resources
export async function indexNonprofitResources() {
  try {
    // Check if Algolia credentials are configured
    if (!import.meta.env.VITE_ALGOLIA_APP_ID || !import.meta.env.VITE_ALGOLIA_ADMIN_KEY) {
      console.warn('Algolia credentials not configured - skipping indexing');
      return false;
    }

    // Configure indexes first
    await configureIndexes();

    // Static non-profit resources for now
    const resources = [
      {
        objectID: 'nonprofit-ai-ethics-checklist',
        title: 'Non-Profit AI Ethics Checklist',
        description: 'A comprehensive checklist to help non-profits implement AI ethically and effectively.',
        content: 'This checklist covers governance, data privacy, fairness, transparency, and implementation strategies for non-profits using AI.',
        url: '/blog/why-non-profits-cant-afford-ignore-ai-any-longer',
        location: 'Omaha, Nebraska',
        organization_type: 'non-profit',
        resource_type: 'checklist',
        published_at: new Date().toISOString()
      },
      {
        objectID: 'midwest-nonprofit-ai-guide',
        title: 'AI Implementation Guide for Midwest Non-Profits',
        description: 'A step-by-step guide for non-profits in the Midwest region to implement AI solutions.',
        content: 'This guide provides practical advice for non-profits in Omaha, Lincoln, Council Bluffs, and throughout the Midwest on implementing AI solutions with limited resources.',
        url: '/blog/why-non-profits-cant-afford-ignore-ai-any-longer',
        location: 'Midwest',
        organization_type: 'non-profit',
        resource_type: 'guide',
        published_at: new Date().toISOString()
      },
      {
        objectID: 'foundation-ai-tools',
        title: 'AI Tools for Foundations and Grantmakers',
        description: 'A curated list of AI tools specifically useful for foundations and grantmakers.',
        content: 'This resource lists AI tools that can help foundations and grantmakers streamline their operations, improve grant application reviews, and measure impact more effectively.',
        url: '/blog/why-non-profits-cant-afford-ignore-ai-any-longer',
        location: 'Omaha, Nebraska',
        organization_type: 'foundation',
        resource_type: 'tool-list',
        published_at: new Date().toISOString()
      }
    ];

    await indexes.nonprofits.saveObjects(resources);
    console.log(`Successfully indexed ${resources.length} non-profit resources`);

    return true;
  } catch (error) {
    console.error('Error indexing non-profit resources:', error);
    return false;
  }
}